import { Fragment, useState, type DragEvent, type ReactNode } from 'react';
import { TASK_DRAG, addLabel, addWords, doneInChoices, earlierToAdd, groupHeading, moveHint, taskGroups, type TaskDrag, type TaskGroup, type TaskPhase } from './taskGroups.ts';

/**
 * A step's tasks by the phase they are done in (design study 31, slice 4, q-r2-early A), on the master's card and a
 * job's panel alike: the earlier phases first, "Done in Design", its own phase last, "In Pre-construction"; where tasks
 * are added, each group with a line to type a task into, each earlier phase with no group yet one click away at the
 * foot ("+ A task in Discovery · Design", opening its group with the line ready), and each task's Done in, a select of
 * its step's phase and the earlier ones (`DoneIn`), moving it to that phase; a task dragged into another group is the
 * shortcut, the same move (HTML5 drag and drop, as the board's tools hand over a kind), never the only way (WCAG 2.1.1,
 * slice 4's final review). Where the list only reads,
 * one group reads as the list always did, with no heading. The rule is `taskGroups.ts`; the rows are each page's own.
 */
export function TaskGroups<K extends string, T>({
  phases,
  own,
  tasks,
  phaseOf,
  idOf,
  nameOf,
  row,
  ownAfter,
  listClass,
  edits,
}: {
  phases: readonly TaskPhase<K>[];
  /** The step's own phase; null for a step in none. */
  own: K | null;
  tasks: readonly T[];
  phaseOf: (t: T) => K | null;
  /** What a dragged row hands over: a job's step id, the master's key. */
  idOf: (t: T) => string;
  /** A task's name as its row shows it: its Done in read out by it. */
  nameOf?: (t: T) => string;
  /**
   * A task's row, an `<li>`; `drag` goes on it while its tasks can be dragged between groups, and `doneIn`, its Done in,
   * in it where its tasks can be moved.
   */
  row: (t: T, group: TaskGroup<K, T>, drag: TaskDrag | null, doneIn: ReactNode | null) => ReactNode;
  /** After its own phase's tasks: the special tasks a job's step carries. */
  ownAfter?: ReactNode;
  listClass: string;
  /** Adding and moving, where the list is changed; none where it only reads. */
  edits?: {
    /** A task typed into a group's line; false keeps the words where they were typed (refused). */
    add: (title: string, phase: K | null, own: boolean) => boolean | Promise<boolean>;
    /** A task set to another phase by its Done in, or dragged into another group. */
    move: (id: string, phase: K, own: boolean) => void;
    busy?: boolean;
  } | null;
}) {
  // Earlier phases opened from the foot to type a task into; the one opened last takes the cursor.
  const [opened, setOpened] = useState<K[]>([]);
  const [dropOn, setDropOn] = useState<K | null>(null);
  const groups = taskGroups(phases, own, tasks, phaseOf, opened);
  const earlier = edits ? earlierToAdd(phases, own, groups) : [];
  const headed = Boolean(edits) || groups.length > 1;
  const moving = Boolean(edits) && groups.length > 1;
  // Its Done in: where the step has earlier phases than its own to do a task in.
  const choices = edits ? doneInChoices(phases, own) : [];
  const doneIn = (t: T, g: TaskGroup<K, T>): ReactNode | null =>
    edits && choices.length > 1 && g.phase !== null ? (
      <DoneIn name={nameOf?.(t) ?? null} value={g.phase} choices={choices} busy={edits.busy} onMove={(phase) => edits.move(idOf(t), phase, phase === own)} />
    ) : null;
  const groupOf = new Map<string, K | null>(groups.flatMap((g) => g.tasks.map((t) => [idOf(t), g.phase] as const)));
  const drag = (t: T): TaskDrag | null =>
    moving
      ? {
          draggable: true,
          onDragStart: (e) => {
            e.dataTransfer.setData(TASK_DRAG, idOf(t));
            e.dataTransfer.effectAllowed = 'move';
          },
        }
      : null;
  const over = (e: DragEvent<HTMLElement>, phase: K) => {
    if (!e.dataTransfer.types.includes(TASK_DRAG)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropOn !== phase) setDropOn(phase);
  };
  const drop = (e: DragEvent<HTMLElement>, g: TaskGroup<K, T>) => {
    const id = e.dataTransfer.getData(TASK_DRAG);
    setDropOn(null);
    if (!id || !edits || g.phase === null || !groupOf.has(id)) return;
    e.preventDefault();
    if (groupOf.get(id) !== g.phase) edits.move(id, g.phase, g.own);
  };

  return (
    <div>
      {groups.map((g) => {
        const heading = headed ? groupHeading(g) : null;
        const target = moving && g.phase !== null ? g.phase : null;
        return (
          <section
            key={g.phase ?? 'none'}
            data-phase={target ?? undefined}
            onDragOver={target ? (e) => over(e, target) : undefined}
            onDragLeave={target ? () => setDropOn(null) : undefined}
            onDrop={target ? (e) => drop(e, g) : undefined}
            className={`mt-2 first:mt-0 ${target && dropOn === target ? 'outline outline-2 outline-offset-2 outline-ink' : ''}`}
          >
            {heading ? <h4 className="label mb-1 font-normal">{heading}</h4> : null}
            {g.tasks.length || (g.own && ownAfter) ? (
              <ul className={listClass}>
                {g.tasks.map((t) => row(t, g, drag(t), doneIn(t, g)))}
                {g.own ? ownAfter : null}
              </ul>
            ) : null}
            {edits ? <AddLine name={g.phase === null ? null : g.name} ready={!g.own && g.phase === opened.at(-1) && !g.tasks.length} busy={edits.busy} onAdd={(title) => edits.add(title, g.phase, g.own)} /> : null}
          </section>
        );
      })}
      {choices.length > 1 && tasks.length ? <p className="meta mt-1.5">{moveHint(moving)}</p> : null}
      {earlier.length ? (
        <p className="meta mt-1.5">
          + A task in{' '}
          {earlier.map((p, i) => (
            <Fragment key={p.key}>
              {i ? ' · ' : ''}
              <button type="button" className="link hit text-ink-2 hover:text-ink" onClick={() => setOpened((o) => [...o.filter((k) => k !== p.key), p.key])}>
                {p.name}
              </button>
            </Fragment>
          ))}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A task's Done in: the phase it is done in, chosen from its step's own and the earlier ones, moving it as a drop does;
 * the phase it is in already moves nothing. Named by its words and, read out, by its task. No state of its own.
 */
export function DoneIn<K extends string>({ name, value, choices, busy, onMove }: { name: string | null; value: K; choices: readonly TaskPhase<K>[]; busy?: boolean; onMove: (phase: K) => void }) {
  return (
    <label className="flex shrink-0 items-center gap-1.5">
      <span className="label !text-[0.625rem]">Done in</span>
      <select
        className="field w-auto py-0 text-[0.8125rem]"
        aria-label={name ? `Done in, for ${name}` : 'Done in'}
        value={value}
        disabled={busy}
        onChange={(e) => {
          const phase = e.target.value as K;
          if (phase !== value) onMove(phase);
        }}
      >
        {choices.map((p) => (
          <option key={p.key} value={p.key}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A group's line: a task typed into it is done in its phase. */
function AddLine({ name, ready, busy, onAdd }: { name: string | null; ready: boolean; busy?: boolean; onAdd: (title: string) => boolean | Promise<boolean> }) {
  const [title, setTitle] = useState('');
  return (
    <form
      className="mt-1 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) return;
        void Promise.resolve(onAdd(t)).then((ok) => ok && setTitle(''));
      }}
    >
      {/* Opened from the foot, it takes the cursor: the one click is all it takes. */}
      <input className="field min-w-0 flex-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={addWords(name)} aria-label={addLabel(name)} maxLength={200} autoFocus={ready} />
      <button type="submit" className="link min-h-11 text-[0.8125rem] text-ink-2 hover:text-ink" disabled={busy || !title.trim()}>
        Add
      </button>
    </form>
  );
}
